import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { useAdminDashboard } from '../hooks/useApi';
import { isClinicAdmin , isStaff } from '../utils/permissions';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';

import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/dateUtils';
import { Users, Camera, GitCompare, UserCog, Plus, ArrowRight, LayoutGrid } from 'lucide-react';
import { hasAnyPermission } from '../utils/permissions';




const Dashboard = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [stats, setStats] = useState({
    totalPatients: 0,
    totalPhotos: 0,
    totalCollages: 0,
    totalStaff: 0,
  });
  const [recentPatients, setRecentPatients] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = () => {
    // TODO: Integrate APIs when available
    // const patients = await getPatientsApi();
    // const photos = await getPhotosApi();
    // const collages = await getCollagesApi();
    // const users = await getUsersApi();

    // For now, set empty/default values
    setStats({
      totalPatients: 0,
      totalPhotos: 0,
      totalCollages: 0,
      totalStaff: 0,
    });

    setRecentPatients([]);
  };

  const {data:dashboardData , isLoading:dashboardLoading} = useAdminDashboard();
const statsdata = dashboardData?.data || [];
const tabledata = dashboardData?.data.recent_patients || [];

  const statCards = [
    {
      title: 'Total Patients',
      value: statsdata.total_patients,
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      title: 'Total Photos',
      value: statsdata.total_photos,
      icon: Camera,
      color: 'from-primary-500 to-primary-600',
      bgColor: 'bg-primary-50',
    },
    {
      title: 'Before/After Collages',
      value: statsdata.total_before_after_photos,
      icon: GitCompare,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
    },
    {
      title: 'Total Collages',
      value: statsdata.total_collage,
      icon: LayoutGrid,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
    },
    ...(isClinicAdmin(currentUser) ? [{
      title: 'Total Staff',
      value: statsdata.total_staff,
      icon: UserCog,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
    }] : [])
  ];

const canViewPatient = hasAnyPermission(currentUser, [
  'patient.list',
  'patient.view',
  'all'
]);


  const quickActions = [
    {
      title: 'Add Patient',
      description: 'Register a new patient',
      icon: Plus,
      action: () => navigate('/patients/add'),
      show: hasAnyPermission(currentUser, ['patient.create','all']),
      color: 'bg-gradient-to-r from-primary-500 to-primary-600 ',
    },
    {
      title: 'Capture Photo',
      description: 'Take patient photos',
      icon: Camera,
      action: () => navigate('/capture'),
      show: hasAnyPermission(currentUser, ['capture.photo','all']),
      color: 'bg-gradient-to-r from-blue-500 to-blue-600',
    },
      {
        title: 'Before/After',
        description: 'Create comparisons',
        icon: GitCompare,
        action: () => navigate('/before-after'),
        show: hasAnyPermission(currentUser, ['before_after.create','all']),
        color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      },
      {
        title: 'Collage',
        description: 'Create collage',
        icon: LayoutGrid,
        action: () => navigate('/collage'),
        show: hasAnyPermission(currentUser, ['collage.create','all']),
        color: 'bg-gradient-to-r from-purple-500 to-purple-600',
      }
  ];

  return (
    <div className="space-y-3 sm:space-y-4 animate-fadeIn">

      {/* Stats Grid */}
      <div className={` grid grid-cols-2 sm:grid-cols-3  ${isStaff(currentUser) ? 'xl:grid-cols-4' : 'xl:grid-cols-5'}  gap-3 sm:gap-4 `}>
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Card key={stat.title} className="overflow-hidden hover:shadow-lg transition-all duration-300">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs sm:text-sm font-medium text-gray-600 flex flex-wrap">{stat.title}</p>
                    <p className="text-2xl sm:text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
                  </div>
                  <div className={`w-12 h-12 sm:w-14 sm:h-14 rounded-2xl ${stat.bgColor} flex items-center justify-center flex-shrink-0 ml-2`}>
                    <div className={`w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br ${stat.color} rounded-xl flex items-center justify-center shadow-md`}>
                      <Icon className="w-4 h-4 sm:w-4 sm:h-4 text-white" />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Quick Actions */}
    
      <div>
        {hasAnyPermission(currentUser, ['patient.create','capture.photo','before_after.create','collage.create','all']) && (
        <h2 className="text-sm sm:text-md font-semibold text-gray-900 mb-3 sm:mb-4">Quick Actions</h2>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-2 xl:grid-cols-4 gap-3 sm:gap-4">
          {quickActions.filter(action => action.show).map((action) => {
            const Icon = action.icon;
            return (
              <Card
                key={action.title}
                className="cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105"
                onClick={action.action}
              >
                <CardContent className="p-4 sm:p-6">
                  <div className="flex items-start gap-3 sm:gap-4">
                    <div className={`w-10 h-10 sm:w-12 sm:h-12 ${action.color} rounded-xl flex items-center justify-center shadow-md flex-shrink-0`}>
                      <Icon className="w-4 h-4 sm:w-4 sm:h-4 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xs sm:text-base font-semibold text-gray-900 flex flex-wrap">{action.title}</h3>
                      <p className="text-xs sm:text-sm text-gray-600 mt-1">{action.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
   

      {/* Recent Patients */}
     
        <h2 className="text-sm sm:text-md font-semibold text-gray-900 mb-3 sm:mb-4">Recent Patients</h2>
    

     
      <Card>
       
        <CardContent className="p-0">
          {tabledata.length === 0 ? (
            <div className="text-center py-6 sm:py-8 text-gray-500 px-4">
              <Users className="w-10 h-10 sm:w-12 sm:h-12 mx-auto mb-3 text-gray-400" />
              <p className="text-sm sm:text-base">No patients yet. Add your first patient to get started.</p>
            </div>
          ) : (
            <div className="overflow-x-auto p-4">
              <Table className="">
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs sm:text-sm">Sr. No.</TableHead>
                  <TableHead className="text-center text-xs sm:text-sm">Patient Name</TableHead>
                  <TableHead className="text-center text-xs sm:text-sm">Patient Age</TableHead>
                  <TableHead className="text-center text-xs sm:text-sm hidden sm:table-cell">Diagnosis</TableHead>
                  <TableHead className="text-center text-xs sm:text-sm hidden md:table-cell">Date Added</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tabledata.map((patient, index) => (
                  <TableRow
                   key={patient.id}
      className={`transition-colors ${
        canViewPatient
          ? 'cursor-pointer hover:bg-gray-50'
          : 'cursor-not-allowed opacity-60'
      }`}
                   onClick={() => {
        if (!canViewPatient) return;
        navigate(`/patients/${patient.id}`);
      }}
                  >
                    <TableCell>
                     
                        <span className="text-black   text-xs sm:text-sm">
                          {index + 1}
                        </span>
                      
                    </TableCell>
                    <TableCell className="font-medium text-center text-xs sm:text-sm">{patient.full_name}</TableCell>
                    <TableCell className="text-gray-600 text-center text-xs sm:text-sm">{patient.age}</TableCell>
                    <TableCell className="text-gray-700 text-center text-xs  sm:text-sm hidden sm:table-cell"><span className="">{patient?.diagnosis}</span></TableCell>
                    <TableCell className="text-right text-center text-gray-500 text-xs sm:text-sm hidden md:table-cell">
                      {formatDate(patient.created_at)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
        </CardContent>
      </Card>
     

    </div>
  );
};

export default Dashboard;
